<template>
    <template v-if="layers.length > 0">
        <Button
            ref="triggerRef"
            class="legend-trigger"
            v-tooltip.top="{
                value: 'Determine what is shown',
                showDelay: 200,
            }"
            severity="secondary"
            size="small"
            aria-haspopup="true"
            aria-controls="layer_legend_popover"
            @click="togglePopover"
        >
            <i class="pi pi-list" />
            <span class="legend-trigger-content">
                <span>Layers</span>
                <span class="layer-indicators">
                    <font-awesome-icon
                        v-for="layer in layers"
                        :key="layer.type"
                        class="layer-indicator-icon"
                        :icon="layer.icon"
                        :style="{
                            color: layer.visible
                                ? layer.color
                                : 'var(--p-text-muted-color)',
                            opacity: layer.visible ? 1 : 0.4,
                        }"
                    />
                </span>
            </span>
        </Button>
        <Popover ref="popoverRef" id="layer_legend_popover">
            <div class="layer-pills">
                <small class="layers-description">
                    Toggle layers to hide or show information throughout
                    MyStekz.
                </small>
                <button
                    v-for="layer in layers"
                    :key="layer.type"
                    class="layer-pill"
                    :class="{ active: layer.visible }"
                    :style="
                        layer.visible
                            ? {
                                  backgroundColor: layer.color,
                                  borderColor: layer.color,
                              }
                            : {}
                    "
                    @click="$emit('toggle', layer.type, !layer.visible)"
                >
                    <font-awesome-icon
                        v-if="layer.icon"
                        class="layer-icon"
                        :icon="layer.icon"
                        :style="layer.visible ? {} : { color: layer.color }"
                    />
                    <span>{{ layer.label }}</span>
                </button>
            </div>
        </Popover>
    </template>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Popover from "primevue/popover";
import { onMounted, ref } from "vue";

import {
    LayerType,
    registerLayerPanelOpener,
} from "@/composables/bpmn-manager/layer";
import { useHotKeys } from "@/composables/hotkeys";

interface Layer {
    type: LayerType;
    label: string;
    color: string;
    icon?: string | string[];
    visible: boolean;
}

defineProps<{
    layers: Layer[];
}>();

defineEmits<{
    toggle: [type: LayerType, visible: boolean];
}>();

const popoverRef = ref<InstanceType<typeof Popover>>();
const triggerRef = ref<InstanceType<typeof Button>>();

// Pass the trigger element explicitly so PrimeVue's isTargetClicked check works
// correctly even when event.currentTarget is null (a known PrimeVue/Vue 4.x quirk
// when onClick propagates through ptmi/inheritAttrs:false).
const togglePopover = () => {
    popoverRef.value?.toggle({
        currentTarget: triggerRef.value?.$el,
    } as Event);
};

onMounted(() => {
    registerLayerPanelOpener(() => {
        setTimeout(() => {
            popoverRef.value?.show(
                { currentTarget: triggerRef.value?.$el } as Event,
                triggerRef.value?.$el
            );
        }, 0);
    });
});

const { addHotKey } = useHotKeys();
addHotKey(
    "l",
    (event) => {
        event.preventDefault();
        togglePopover();
    },
    "Toggle layers panel",
    "BPMN"
);
</script>

<style scoped lang="scss">
.legend-trigger {
    text-decoration: none;
}

.legend-trigger-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
}

.layer-indicators {
    display: flex;
    gap: 3px;
    align-items: center;
}

.layer-indicator-icon {
    font-size: 0.55rem;
    transition:
        color 0.15s ease,
        opacity 0.15s ease;
}

.layer-pills {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    min-width: 12rem;
}

.layers-description {
    font-size: 11px;
    color: var(--p-text-muted-color);
    margin: 0;
    line-height: 1.4;
    max-width: 12rem;
}

.layer-pill {
    width: 100%;
    padding: 3px 10px;
    border: 1px solid var(--p-content-border-color);
    background: transparent;
    color: var(--p-text-muted-color);
    text-transform: uppercase;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        background: var(--surface-background-mild);
        color: var(--p-content-color);
    }

    &.active {
        color: rgba(255, 255, 255, 0.75);

        &:hover {
            opacity: 0.9;
        }
    }
}

.layer-icon {
    font-size: 0.95rem;
}
</style>

<style lang="scss">
/* Unscoped: Popover teleports to body, so the scoped attribute can't reach it.
   Targeted by id to avoid affecting other Popover instances. */
#layer_legend_popover {
    margin-block-start: 0;

    &.p-popover::before,
    &.p-popover::after {
        display: none;
    }
}
</style>
